import { Box, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { useVizImage } from '../../documents/SectionVisualizationsDialog';
import type { DocumentVisualization } from '../../../core/types/data-models';

/** One visualization thumbnail in the Visual Artifact Views gallery — image preview, document name, and section title. */
export function VisualViewCard({
  viz,
  docName,
  sectionTitle,
  onClick,
}: {
  viz: DocumentVisualization;
  docName: string | undefined;
  sectionTitle: string | undefined;
  onClick: () => void;
}) {
  const { data } = useVizImage(viz.id, true);
  const src = data?.imageData ? `data:${data.mimeType};base64,${data.imageData}` : undefined;

  return (
    <GlassPanel
      hover
      onClick={onClick}
      sx={{
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover .viz-preview-overlay': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          height: 120,
          position: 'relative',
          bgcolor: 'var(--color-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {src
          ? <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Skeleton variant="rectangular" width="100%" height="100%" />}
        <Box
          className="viz-preview-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            backdropFilter: 'blur(1px)',
            opacity: 0,
            transition: 'opacity var(--transition)',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'grey.900',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            Preview
          </Box>
        </Box>
      </Box>
      <Box sx={{ p: 1.25, pt: 0.75 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {docName ?? 'Untitled document'}
        </Typography>
        {sectionTitle && (
          <Typography
            variant="caption"
            noWrap
            sx={{ display: 'block', fontFamily: 'var(--font-mono)', color: 'text.secondary' }}
          >
            {sectionTitle}
          </Typography>
        )}
      </Box>
    </GlassPanel>
  );
}
